// Recursive-descent parser for loop placeholder expressions.
// Example: `${(i * 2) + j}` is evaluated with precedence here.
import { applyBinaryOperator, resolvePrimaryToken } from './placeholder-expression-operations'
import type { LoopExpressionState, LoopExpressionToken, LoopExpressionValue } from './placeholder-expression-types'

// Parse one full expression value from the token stream.
export function parseLoopExpressionValue(state: LoopExpressionState): LoopExpressionValue | null {
  // Return the parsed additive expression result.
  return parseAdditive(state)
}

// Parse one additive expression with `+` and `-`.
function parseAdditive(state: LoopExpressionState): LoopExpressionValue | null {
  let current = parseMultiplicative(state)

  // Stop when the left-hand side could not be parsed.
  if (!current) {
    // Return null for invalid additive expressions.
    return null
  }

  // Consume additive operators in left-to-right order.
  while (true) {
    const operator = peekToken(state)
    if (!operator || (operator.kind !== 'plus' && operator.kind !== 'minus')) break
    state.index += 1
    const right = parseMultiplicative(state)
    if (!right) {
      // Return null for missing right-hand additive operands.
      return null
    }
    current = applyBinaryOperator(current, operator.kind, right)
    if (!current) {
      // Return null for incompatible additive operand pairs.
      return null
    }
  }

  // Return the parsed additive expression value.
  return current
}

// Parse one multiplicative expression with `*` and `/`.
function parseMultiplicative(state: LoopExpressionState): LoopExpressionValue | null {
  let current = parseUnary(state)

  // Stop when the left-hand side could not be parsed.
  if (!current) {
    // Return null for invalid multiplicative expressions.
    return null
  }

  // Consume multiplicative operators in left-to-right order.
  while (true) {
    const operator = peekToken(state)
    if (!operator || (operator.kind !== 'star' && operator.kind !== 'slash')) break
    state.index += 1
    const right = parseUnary(state)
    if (!right) {
      // Return null for missing right-hand multiplicative operands.
      return null
    }
    current = applyBinaryOperator(current, operator.kind, right)
    if (!current) {
      // Return null for incompatible multiplicative operand pairs.
      return null
    }
  }

  // Return the parsed multiplicative expression value.
  return current
}

// Parse unary `+` and `-` operators.
function parseUnary(state: LoopExpressionState): LoopExpressionValue | null {
  const token = peekToken(state)

  // Continue with primary parsing when no unary operator is present.
  if (!token || (token.kind !== 'plus' && token.kind !== 'minus')) {
    // Return the primary expression value directly.
    return parsePrimary(state)
  }

  state.index += 1
  const value = parseUnary(state)

  // Stop when the unary operand could not be parsed.
  if (!value || value.kind !== 'number') {
    // Return null for non-numeric unary operations.
    return null
  }

  // Return the unary-adjusted numeric value.
  return token.kind === 'minus' ? { kind: 'number', value: -value.value, unit: value.unit } : value
}

// Parse one primary expression token or parenthesized group.
function parsePrimary(state: LoopExpressionState): LoopExpressionValue | null {
  const token = peekToken(state)

  // Stop when no primary token is available.
  if (!token) {
    // Return null for missing primary expressions.
    return null
  }

  // Parse one parenthesized sub-expression.
  if (token.kind === 'paren-open') {
    state.index += 1
    const inner = parseAdditive(state)
    if (!inner || peekToken(state)?.kind !== 'paren-close') {
      // Return null for malformed parenthesized expressions.
      return null
    }
    state.index += 1
    // Return the inner grouped expression result.
    return inner
  }

  state.index += 1

  // Resolve one literal or identifier token into a runtime value.
  return resolvePrimaryToken(token, state.scope, state.arrays, state.variables, state.objectVariables)
}

// Peek at the current parser token without consuming it.
function peekToken(state: LoopExpressionState): LoopExpressionToken | null {
  // Return the current token or null at end-of-stream.
  return state.tokens[state.index] ?? null
}
